import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  HardHat, Search, Loader2, ChevronRight, Settings, Mail
} from 'lucide-react';
import SEO from '../seo/SEO';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const NonITCareersPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [nonItJobs, setNonItJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const snap = await getDocs(collection(db, 'jobs'));
      const fetched = [];
      snap.forEach(docSnap => {
        fetched.push({ id: docSnap.id, ...docSnap.data() });
      });

      // Filter for roles that are NOT 'IT'
      const filteredForNonIT = fetched.filter(job => 
        job.type && job.type.toString().trim().toUpperCase() !== 'IT'
      );

      setNonItJobs(filteredForNonIT);
    } catch (error) {
      console.error("Error fetching non-IT jobs from Firebase:", error);
      setNonItJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = ['All', ...new Set(nonItJobs.map(job => job.category).filter(Boolean))];

  const filteredJobs = nonItJobs.filter(job => 
    (filterCategory === 'All' || job.category === filterCategory) &&
    job.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCardClick = (job) => {
    navigate(`/careers/${job.id}`, { state: { job } });
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-32">
      <SEO
        title="Non-IT Careers & Skilled Trades | GlobalEPRC"
        description="Explore Non-IT job openings with GlobalEPRC across skilled, semi-skilled and industrial manpower roles in major global sectors."
        path="/non-it-careers"
      />
      <main className="container mx-auto px-4 md:px-8 pb-20">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full mb-6 font-bold text-xs uppercase tracking-widest">
            <HardHat className="h-4 w-4" /> Global Workforce Solutions
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">
            Non-IT <span className="text-gold-gradient">Careers</span>
          </h1>
          <p className="text-slate-600">
            GlobalEPRC specializes in supplying verified skilled, semi-skilled, and industrial trade professionals across major international infrastructure, facilities, and manufacturing sectors.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white p-4 rounded-3xl shadow-xl border border-slate-100 mb-12 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative w-full md:w-1/2">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
            <input 
              type="text" 
              placeholder="Search trades (e.g. Welder, Electrician, HVAC)..."
              className="w-full pl-12 pr-4 py-4 bg-slate-50 rounded-2xl focus:ring-2 focus:ring-amber-500 outline-none transition-all text-sm"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-1/2 overflow-x-auto pb-2">
            {categories.map(cat => (
              <button 
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-5 py-2 rounded-full whitespace-nowrap text-sm font-bold transition-all ${
                  filterCategory === cat 
                    ? 'bg-gold-gradient text-slate-950 shadow-md font-black' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Job Grid */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-12 w-12 text-amber-500 animate-spin mb-4" />
            <p className="text-slate-500 font-medium">Syncing with workforce database...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredJobs.length > 0 ? filteredJobs.map(job => (
              <div 
                key={job.id} 
                onClick={() => handleCardClick(job)}
                className="bg-white p-8 rounded-[2.5rem] shadow-md hover:shadow-xl border border-slate-100 group flex flex-col justify-between cursor-pointer transition-all duration-300 transform hover:-translate-y-1"
              >
                <div>
                  <div className="bg-slate-50 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold-gradient transition-all">
                    <Settings className="h-6 w-6 text-amber-600 group-hover:text-slate-950 transition-colors" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-slate-900 group-hover:text-amber-700 transition-colors">
                    {job.title}
                  </h3>
                  <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">{job.category}</span>
                  <p className="text-slate-500 text-sm mt-4 mb-6 line-clamp-3 leading-relaxed">{job.desc}</p>
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="flex items-center gap-2 font-bold text-slate-900 group-hover:text-amber-600 transition-colors text-sm">
                    View Details & Apply 
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-1.5 transition-transform" />
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md">
                    Verified Trade
                  </span>
                </div>
              </div>
            )) : (
              <div className="col-span-full text-center py-20 bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
                <HardHat className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-800 mb-2">No Active Trade Openings Found</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">
                  {searchTerm 
                    ? `No trade postings match "${searchTerm}". Try another keyword or clear filters.`
                    : 'We are currently synchronizing upcoming project quotas and client requirements. You can register your trade credentials with us directly.'}
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm rounded-xl transition shadow"
                >
                  <Mail className="h-4 w-4" /> Register Your Trade Profile
                </Link>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Gallery Section */}
      <div className="container mx-auto px-4 md:px-8 mb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <img src="/non-it3.jpeg" className="rounded-3xl shadow-lg h-48 w-full object-cover" alt="Construction" />
          <img src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&q=80&w=400" className="rounded-3xl shadow-lg mt-8 h-48 w-full object-cover" alt="Technical" />
          <img src="/non-it1.jpeg" className="rounded-3xl shadow-lg h-48 w-full object-cover" alt="Industrial" />
          <img src="https://images.unsplash.com/photo-1590674899484-d5640e854abe?auto=format&fit=crop&q=80&w=400" className="rounded-3xl shadow-lg mt-8 h-48 w-full object-cover" alt="Specialized" />
        </div>
      </div>
    </div>
  );
};

export default NonITCareersPage;
